import { Editor } from '@tiptap/react';

// Font size uses Word's two-level model: the paragraph/heading carries a *base*
// size (the `blockFontSize` attribute, which also shrinks the line box so
// spacing follows the text), and a `textStyle` span size overrides individual
// words on top of that base.
//
// Routing mirrors Word: a size change covering a whole block — or just the caret
// — sets the block base (which also couples line spacing); a partial in-block
// selection sets a span override for those words only.

const selectionCoversWholeBlocks = (editor: Editor): boolean => {
  const { empty, $from, $to } = editor.state.selection;
  if (empty) return true;
  return $from.parentOffset === 0 && $to.parentOffset === $to.parent.content.size;
};

// Block base used as the spacing-driving size.
export const getBlockFontSize = (editor: Editor): string | undefined =>
  editor.getAttributes('paragraph').fontSize as string | undefined;

// Effective size shown in the toolbar and matched against the presets: a span
// override (caret/selection inside a sized word) takes precedence over the base.
export const getActiveFontSize = (editor: Editor): string | undefined =>
  (editor.getAttributes('textStyle').fontSize as string | undefined) ??
  getBlockFontSize(editor);

// Apply a size (e.g. `"12px"`) or clear it (`null` = default).
export const applyFontSize = (editor: Editor, size: string | null): void => {
  const chain = editor.chain().focus();
  if (!size) {
    chain.unsetBlockFontSize().unsetFontSize().run();
    return;
  }
  if (selectionCoversWholeBlocks(editor)) {
    // The base wins uniformly — drop any per-word override first.
    chain.unsetFontSize().setBlockFontSize(size).run();
  } else {
    // Selection → size just those words.
    chain.setFontSize(size).run();
  }
};
